import React, { useState, useEffect, useCallback, useRef } from 'react';
import useStudentInteraction, { STUDENT_PARTICIPATION_UPDATE_EVENT, StudentParticipationUpdateEvent } from 'hooks/useStudentInteraction';
import { StudentParticipation } from 'types/common';
import './styles.css';

interface StudentInteractionPanelProps {
  classId: number | string;
}

// Helper function to get participation level class
const getParticipationLevelClass = (percentage: number): string => {
  if (percentage >= 70) return 'high-participation';
  if (percentage >= 40) return 'moderate-participation';
  return 'low-participation';
};

// Helper function to get participation level label
const getParticipationLevelLabel = (percentage: number): string => {
  if (percentage >= 70) return 'High';
  if (percentage >= 40) return 'Moderate';
  return 'Low';
};

const StudentInteractionPanel: React.FC<StudentInteractionPanelProps> = ({ classId }) => {
  // State to track the selected window index
  const [selectedWindowIndex, setSelectedWindowIndex] = useState<number | null>(null);
  const [currentTimeWindowIndex, setCurrentTimeWindowIndex] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  
  // Ref to store student data for direct DOM manipulation
  const studentDataRef = useRef<Map<string | number, StudentParticipation>>(new Map());
  // Ref to store DOM elements for the indicators
  const indicatorElementsRef = useRef<Map<string | number, HTMLDivElement>>(new Map());
  // Ref to store the interaction list element
  const interactionListRef = useRef<HTMLDivElement>(null);
  
  // Use a 10-second polling interval for the API
  const { interactionData, isLoading, error, isMockData, refetch } = useStudentInteraction(classId, 10000);

  // Function to get the current time window from the API response
  const updateCurrentTimeWindow = useCallback(() => {
    if (interactionData) {
      // Always use the currentWindow from the API response if available
      if (interactionData.currentWindow !== undefined && interactionData.currentWindow !== null) {
        // The API returns a 1-based index, but we use 0-based indices in the component
        const windowIndex = interactionData.currentWindow - 1;
        
        // Ensure the window index is within bounds
        if (windowIndex >= 0 && windowIndex < interactionData.fifteenMinuteWindows.length) {
          // Force update the current time window index
          setCurrentTimeWindowIndex(windowIndex);
        } else if (windowIndex >= interactionData.fifteenMinuteWindows.length) {
          // If the window index is beyond the available windows, use the last window
          setCurrentTimeWindowIndex(interactionData.fifteenMinuteWindows.length - 1);
        } else {
          // If the window index is negative, use the first window
          setCurrentTimeWindowIndex(0);
        }
      } else {
        // Fallback to the first window if currentWindow is not provided
        setCurrentTimeWindowIndex(0);
      }
    }
  }, [interactionData]);
  
  // Update the current time window when data changes
  useEffect(() => {
    updateCurrentTimeWindow();
  }, [updateCurrentTimeWindow]);
  
  // Always update selected window index to match the current window from API
  useEffect(() => {
    if (currentTimeWindowIndex !== null) {
      // Force update the selected window index to match the current window
      setSelectedWindowIndex(currentTimeWindowIndex);
    }
  }, [currentTimeWindowIndex, interactionData?.currentWindow]);
  
  // Function to update the indicator positions directly in the DOM
  const updateIndicatorPositions = useCallback(() => {
    if (!interactionData) return;
    
    // Get the current window data
    const effectiveWindowIndex = selectedWindowIndex !== null ? selectedWindowIndex : currentTimeWindowIndex;
    const safeWindowIndex = Math.min(effectiveWindowIndex, interactionData.fifteenMinuteWindows.length - 1);
    const currentWindow = interactionData.fifteenMinuteWindows[safeWindowIndex];
    
    if (!currentWindow || !currentWindow.studentParticipation) return;
    
    // Update the student data ref
    const newStudentDataMap = new Map<string | number, StudentParticipation>();
    currentWindow.studentParticipation.forEach(student => {
      newStudentDataMap.set(student.studentId, student);
    });
    studentDataRef.current = newStudentDataMap;
    
    // Update the indicator positions
    indicatorElementsRef.current.forEach((element, studentId) => {
      const student = studentDataRef.current.get(studentId);
      if (student && element) {
        // Update position
        const position = Math.max(0, Math.min(100, student.participationPercentage));
        element.style.left = `${position}%`;
        
        // Update class
        const levelClass = getParticipationLevelClass(student.participationPercentage);
        element.className = `meter-indicator ${levelClass}`;
        
        // Update percentage text
        const valueElement = element.querySelector('.indicator-value');
        if (valueElement) {
          valueElement.textContent = `${student.participationPercentage}%`;
        }
        
        // Update label
        const parentItem = element.closest('.student-interaction-item');
        if (parentItem) {
          const labelElement = parentItem.querySelector('.participation-level-label');
          if (labelElement) {
            labelElement.textContent = getParticipationLevelLabel(student.participationPercentage);
          }
        }
      }
    });
  }, [interactionData, selectedWindowIndex, currentTimeWindowIndex]);
  
  // Update indicator positions when data or selected window changes
  useEffect(() => {
    updateIndicatorPositions();
  }, [updateIndicatorPositions]);
  
  // Listen for student participation updates
  useEffect(() => {
    const handleStudentParticipationUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<StudentParticipationUpdateEvent>;
      const { windowIndex, students } = customEvent.detail;
      
      // Only update if this is the currently selected window
      const effectiveWindowIndex = selectedWindowIndex !== null ? selectedWindowIndex : currentTimeWindowIndex;
      const safeWindowIndex = interactionData ? 
        Math.min(effectiveWindowIndex, interactionData.fifteenMinuteWindows.length - 1) : 0;
      
      if (windowIndex === safeWindowIndex) {
        // Update the student data ref
        const newStudentDataMap = new Map<string | number, StudentParticipation>();
        students.forEach(student => {
          newStudentDataMap.set(student.studentId, student);
        });
        
        // Update the indicator positions directly
        indicatorElementsRef.current.forEach((element, studentId) => {
          const student = newStudentDataMap.get(studentId);
          if (student && element) {
            // Update position
            const position = Math.max(0, Math.min(100, student.participationPercentage));
            element.style.left = `${position}%`;
            
            // Update class
            const levelClass = getParticipationLevelClass(student.participationPercentage);
            element.className = `meter-indicator ${levelClass}`;
            
            // Update percentage text
            const valueElement = element.querySelector('.indicator-value');
            if (valueElement) {
              valueElement.textContent = `${student.participationPercentage}%`;
            }
            
            // Update label
            const parentItem = element.closest('.student-interaction-item');
            if (parentItem) {
              const labelElement = parentItem.querySelector('.participation-level-label');
              if (labelElement) {
                labelElement.textContent = getParticipationLevelLabel(student.participationPercentage);
              }
            }
          }
        });
      }
    };
    
    // Add event listener
    document.addEventListener(STUDENT_PARTICIPATION_UPDATE_EVENT, handleStudentParticipationUpdate);
    
    // Clean up
    return () => {
      document.removeEventListener(STUDENT_PARTICIPATION_UPDATE_EVENT, handleStudentParticipationUpdate);
    };
  }, [selectedWindowIndex, currentTimeWindowIndex, interactionData]);
  
  // Function to register an indicator element
  const registerIndicator = (studentId: string | number, element: HTMLDivElement | null) => {
    if (element) {
      indicatorElementsRef.current.set(studentId, element);
    }
  };
  
  // Function to create the initial student list
  const createStudentList = useCallback(() => {
    if (!interactionData) return null;
    
    // Get the current window data
    const effectiveWindowIndex = selectedWindowIndex !== null ? selectedWindowIndex : currentTimeWindowIndex;
    const safeWindowIndex = Math.min(effectiveWindowIndex, interactionData.fifteenMinuteWindows.length - 1);
    const currentWindow = interactionData.fifteenMinuteWindows[safeWindowIndex];
    
    if (!currentWindow || !currentWindow.studentParticipation || currentWindow.studentParticipation.length === 0) {
      return (
        <div className="student-interaction-panel empty">
          No student participation data available for this time window
        </div>
      );
    }
    
    // Sort students by participation percentage (highest first)
    const sortedStudents = [...currentWindow.studentParticipation].sort(
      (a, b) => b.participationPercentage - a.participationPercentage
    );
    
    // Update the student data ref
    const newStudentDataMap = new Map<string | number, StudentParticipation>();
    sortedStudents.forEach(student => {
      newStudentDataMap.set(student.studentId, student);
    });
    studentDataRef.current = newStudentDataMap;
    
    return (
      <div className="interaction-list" ref={interactionListRef}>
        {sortedStudents.map((student: StudentParticipation) => (
          <div key={student.studentId} className="student-interaction-item">
            <div className="student-name">{student.studentName}</div>
            
            <div className="participation-meter-container">
              <div className="participation-meter">
                <div className="meter-scale">
                  <div className="meter-section low-section"></div>
                  <div className="meter-section moderate-section"></div>
                  <div className="meter-section high-section"></div>
                </div>
                <div 
                  className={`meter-indicator ${getParticipationLevelClass(student.participationPercentage)}`}
                  style={{ left: `${Math.max(0, Math.min(100, student.participationPercentage))}%` }}
                  ref={(el) => registerIndicator(student.studentId, el)}
                  onMouseEnter={(e) => {
                    // Show the percentage value on hover
                    const target = e.currentTarget.querySelector('.indicator-value');
                    if (target) {
                      (target as HTMLElement).style.opacity = '1';
                      (target as HTMLElement).style.transform = 'translateX(-50%) translateY(0)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    // Hide the percentage value when not hovering
                    const target = e.currentTarget.querySelector('.indicator-value');
                    if (target) {
                      (target as HTMLElement).style.opacity = '0';
                      (target as HTMLElement).style.transform = 'translateX(-50%) translateY(4px)';
                    }
                  }}
                >
                  <span className="indicator-value">{student.participationPercentage}%</span>
                </div>
              </div>
              {/* Participation level label removed as requested */}
            </div>
          </div>
        ))}
      </div>
    );
  }, [interactionData, selectedWindowIndex, currentTimeWindowIndex]);
  
  // Early return for loading and error states
  if (isLoading) {
    return <div className="student-interaction-panel loading">Loading interaction data...</div>;
  }

  if (error || !interactionData) {
    return <div className="student-interaction-panel error">Unable to load student interaction data</div>;
  }

  // Check if there are any windows available
  if (!interactionData.fifteenMinuteWindows || interactionData.fifteenMinuteWindows.length === 0) {
    return <div className="student-interaction-panel empty">No time windows available yet</div>;
  }

  // Use the selected window index if available, otherwise use the current time window
  const effectiveWindowIndex = selectedWindowIndex !== null ? selectedWindowIndex : currentTimeWindowIndex;
  
  // Ensure the effective window index is within bounds
  const safeWindowIndex = Math.min(effectiveWindowIndex, interactionData.fifteenMinuteWindows.length - 1);
  
  // Get the current window data
  const currentWindow = interactionData.fifteenMinuteWindows[safeWindowIndex];
    
  // Handle window selection change
  const handleWindowChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const index = parseInt(event.target.value, 10);
    setSelectedWindowIndex(index);
  };

  if (!currentWindow) {
    return <div className="student-interaction-panel empty">No interaction data available yet</div>;
  }

  return (
    <div className={`student-interaction-panel ${isMockData ? 'mock-data' : ''}`}>
      <div className="panel-header">
        <div className="panel-title-container">
          <h3 className="panel-title">
            Student Participation
            {isMockData && <span className="mock-data-badge">Demo Data</span>}
          </h3>
          
          <button 
            className={`refresh-button ${isRefreshing ? 'refreshing' : ''}`}
            onClick={() => {
              setIsRefreshing(true);
              refetch().finally(() => {
                setTimeout(() => setIsRefreshing(false), 500);
              });
            }}
            disabled={isRefreshing}
            title="Refresh data"
          >
            {isRefreshing ? '⟳' : '↺'}
          </button>
        </div>
        
        <div className="window-selector">
          <select 
            value={safeWindowIndex} 
            onChange={handleWindowChange}
            className="window-select"
          >
            {interactionData.fifteenMinuteWindows.map((window, index) => (
              <option key={index} value={index}>
                {window.windowName} {index === currentTimeWindowIndex ? '(Current)' : ''}
              </option>
            ))}
          </select>
          {safeWindowIndex !== currentTimeWindowIndex && (
            <button 
              className="reset-window-button" 
              onClick={() => setSelectedWindowIndex(currentTimeWindowIndex)}
              title="Reset to current time window"
            >
              ↻
            </button>
          )}
        </div>
      </div>
      
      {createStudentList()}
    </div>
  );
};

export default StudentInteractionPanel;